import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid2'
import InfoIcon from '@mui/icons-material/Info'
import UpdateIcon from '@mui/icons-material/Update'
import ErrorOverlay from 'Components/General/ErrorOverlay'
import { orderBy } from 'lodash'
import { useCallback, useState, useEffect, ComponentType } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ICourse,
  IEducation,
  IEmployer,
  ILeisureActivity,
  ILanguageSkill,
  IPersonCertificate,
  IPersonCompetence,
  IPersonSkillOrIndustryOrRole,
  IProject,
  IReference,
} from 'types/cvsInterfaces'
import { IError } from 'types/error'
import CaleoIconButton from '../IconButtons/CaleoIconButton'
import { isEqual } from 'lodash'
import AddIcon from '@mui/icons-material/Add'
import { useUser } from 'hooks'
import { Data, IItemDraftHandle } from 'types/hocTypes'

type ProfileCardProps = {
  items?: T[]
  data?: Data
  header: string
  editable?: boolean
  hideAddButton?: boolean
  /** Hides the card's title, e.g. when the page already has a heading for it. The header actions stay. */
  hideTitle?: boolean
  /**
   * Dialog drafts (opt-in, used by the CV wizard). If a dialog of this card has an unsaved draft when the card
   * mounts, that dialog is reopened; the card passes `itemDraft.forItem(editedItem)` to its dialog as `draft`.
   */
  itemDraft?: IItemDraftHandle
  update?: boolean
  details?: boolean
  [key: string]: unknown
}

type T =
  | IReference
  | IPersonSkillOrIndustryOrRole
  | ILanguageSkill
  | IProject
  | IEmployer
  | ILeisureActivity
  | ICourse
  | IEducation
  | IPersonCertificate
  | IPersonCompetence

/**
 * Profile card higher order component.
 *
 * @param Component - The component to wrap.
 * @returns The wrapped component.
 * @notExported
 */
const withProfileCard = (Component: ComponentType<ProfileCardProps>) => {
  const ProfileCard = (props: ProfileCardProps) => {
    const [items, setItems] = useState<T[]>(props.items ?? [])
    const [data, setData] = useState<Data | undefined>(props.data)
    const [editedItem, setEditedItem] = useState<T | null | undefined>(() => {
      const pending = props.itemDraft?.getPendingItem()
      return pending === undefined ? undefined : (pending as T | null)
    })
    const [backendError, setBackendError] = useState<IError>()
    const [detailsOpen, setDetailsOpen] = useState<boolean>(false)
    const [updateOpen, setUpdateOpen] = useState<boolean>(false)
    const { t, i18n } = useTranslation()
    const { features } = useUser()

    useEffect(() => {
      setItems(prev => (isEqual(props.items, prev) ? prev : (props.items ?? [])))
    }, [props.items])

    useEffect(() => {
      setData(prev => (isEqual(props.data, prev) ? prev : props.data))
    }, [props.data])

    const replaceOrAddItem = (item: T | { deleted: number }): T[] => {
      let newItems: T[] = [...items]
      const targetId = 'deleted' in item ? item.deleted : item.id

      if (!('deleted' in item)) {
        const index = newItems.findIndex(existing => existing.id === targetId)
        if (index > -1) {
          newItems[index] = item
        } else {
          newItems.push(item)
        }
      } else {
        newItems = newItems.filter(existing => existing.id !== targetId)
      }

      setItems(newItems)
      return newItems
    }

    const replaceOrAddItems = (incomingItems: T[]): T[] => {
      const newItems: T[] = [...items]

      for (const item of incomingItems) {
        const index = newItems.findIndex(existing => existing.id === item.id)

        if (index > -1) {
          newItems[index] = item
        } else {
          newItems.push(item)
        }
      }

      setItems(newItems)
      return newItems
    }

    const replaceOrAddData = (
      item: T | { deleted: number },
      type: 'education' | 'courses' | 'certificates' | 'competences'
    ): Data => {
      const currentData: Data = data ?? { education: [], courses: [], certificates: [], competences: [] }
      const newData: Data = {
        education: [],
        courses: [],
        certificates: [],
        competences: [],
      }
      const targetId = 'deleted' in item ? item.deleted : item.id
      let itemData

      if (type === 'education') {
        itemData = item as IEducation
        newData.courses = [...currentData.courses]
        newData.certificates = [...currentData.certificates]
        newData.competences = [...currentData.competences]
      } else if (type === 'courses') {
        itemData = item as ICourse
        newData.education = [...currentData.education]
        newData.certificates = [...currentData.certificates]
        newData.competences = [...currentData.competences]
      } else if (type === 'certificates') {
        itemData = item as IPersonCertificate
        newData.education = [...currentData.education]
        newData.courses = [...currentData.courses]
        newData.competences = [...currentData.competences]
      } else if (type === 'competences' && features.includes('competences')) {
        itemData = item as IPersonCompetence
        newData.education = [...currentData.education]
        newData.courses = [...currentData.courses]
        newData.certificates = [...currentData.certificates]
      }

      if (itemData === undefined) return currentData

      type DataItem = IEducation | ICourse | IPersonCertificate | IPersonCompetence
      const targetArray = newData[type] as DataItem[]

      let found = false
      for (const existing of currentData[type]) {
        if (existing.id === targetId) {
          if (!('deleted' in itemData)) {
            targetArray.push(itemData)
          }
          found = true
        } else {
          targetArray.push(existing as DataItem)
        }
      }
      if (!found && !('deleted' in itemData)) {
        targetArray.push(itemData)
      }

      setData(newData)
      return newData
    }

    const getSortedItems = useCallback(() => {
      if (
        props.header === 'skills' ||
        props.header === 'industries' ||
        props.header === 'roles' ||
        props.header === 'language'
      ) {
        return orderBy(items, ['level'], ['desc'])
      } else if (props.header === 'project' || props.header === 'employer') {
        return orderBy(items, ['endDate', 'startDate'], ['desc', 'desc'])
      }
      return items
    }, [items, props.header])

    const getAddButton = useCallback(() => {
      return (
        <Button
          data-testid={`button-profile-card-add-${props.header}`}
          onClick={() => setEditedItem(null)}
          color="primary"
          variant="contained"
          startIcon={<AddIcon />}
        >
          {t(`profile.${props.header}.add`)}
        </Button>
      )
    }, [props.header, t])

    const getDetailsButton = useCallback(() => {
      return (
        <CaleoIconButton
          testId={`button-profile-card-details-${props.header}`}
          tooltip={t('profile.skill.info')}
          clickAction={() => setDetailsOpen(true)}
          size="medium"
          icon={<InfoIcon fontSize="large" />}
        />
      )
    }, [props.header, t])

    const getUpdateButton = useCallback(() => {
      return (
        <CaleoIconButton
          testId={`button-profile-card-update-${props.header}`}
          tooltip={t('profile.skill.update')}
          clickAction={() => setUpdateOpen(true)}
          size="medium"
          icon={<UpdateIcon fontSize="large" />}
        />
      )
    }, [props.header, t])

    const getHeaderAction = useCallback(() => {
      return (
        <span style={{ display: 'flex', flexFlow: 'row-reverse', alignItems: 'center' }}>
          {props.editable && !props.hideAddButton && getAddButton()}
          {props.update && props.editable && getUpdateButton()}
          {props.details && getDetailsButton()}
        </span>
      )
    }, [
      getAddButton,
      getDetailsButton,
      getUpdateButton,
      props.details,
      props.editable,
      props.hideAddButton,
      props.update,
    ])

    if (backendError && backendError.name !== 'CanceledError' && backendError.name !== 'AbortError') {
      return <ErrorOverlay error={backendError} setOpen={setBackendError} />
    }

    return (
      <Grid container mt={props.hideTitle ? 0 : 4}>
        <Grid size={12}>
          <Grid container justifyContent={props.hideTitle ? 'flex-end' : 'space-between'} size={12}>
            {!props.hideTitle && (
              <Grid>
                <Typography variant="h3" gutterBottom fontWeight="bold">
                  {t(`${props.header}.title`)}
                </Typography>
              </Grid>
            )}
            <Grid>{getHeaderAction()}</Grid>
          </Grid>
        </Grid>
        <Grid size={12}>
          <Component
            {...props}
            closeItem={() => setEditedItem(undefined)}
            replaceOrAddItem={replaceOrAddItem}
            replaceOrAddItems={replaceOrAddItems}
            replaceOrAddData={replaceOrAddData}
            items={getSortedItems()}
            data={data}
            openItem={(item: T) => setEditedItem(item)}
            editedItem={editedItem}
            closeDetails={() => setDetailsOpen(false)}
            detailsOpen={detailsOpen}
            closeUpdate={() => setUpdateOpen(false)}
            updateOpen={updateOpen}
            languageKey={i18n.language}
          />
        </Grid>
      </Grid>
    )
  }
  return ProfileCard
}

export default withProfileCard
